/**
 * Catálogo de la Guía Legal de Conceptos de Nómina.
 *
 * Es SÓLO documentación para el usuario. Explica, por clave del catálogo SAT:
 *   · qué es el concepto según la Ley Federal del Trabajo,
 *   · si grava o exenta según la Ley del ISR y la Ley del Seguro Social,
 *   · cómo capturarlo en Biwo y con qué fórmulas.
 *
 * Las fórmulas de los conceptos con `implementado: true` están copiadas LITERAL de
 * CONCEPTOS_DE_LEY en erp_backend/Controllers/Nomina/wizardNomina.js, que es lo que el
 * asistente de configuración siembra de verdad. Si allá cambia una fórmula, hay que
 * reflejarlo aquí a mano o la guía va a enseñar algo distinto a lo que el sistema hace.
 *
 * Los conceptos con `implementado: false` tienen exención en la ley pero el asistente NO
 * los siembra: sus fórmulas son una propuesta, no una copia, y hay que capturarlas a mano.
 *
 * Las variables de las fórmulas tienen que existir en `construirScope`
 * (erp_backend/Controllers/Prenomina/calculo.controllers.js) y estar documentadas en
 * ../GuiaFormulas/catalogoFormulas.js. Una variable que no existe vale 0 en silencio.
 */

// Sitio oficial de la Cámara de Diputados. Misma liga que usa AsistenteDepreciacionLISR.
export const LIGAS = {
    LFT: "https://www.diputados.gob.mx/LeyesBiblio/pdf/LFT.pdf",
    LISR: "https://www.diputados.gob.mx/LeyesBiblio/pdf/LISR.pdf",
    LSS: "https://www.diputados.gob.mx/LeyesBiblio/pdf/LSS.pdf",
};

export const NOMBRE_LEY = {
    LFT: "Ley Federal del Trabajo",
    LISR: "Ley del Impuesto sobre la Renta",
    LSS: "Ley del Seguro Social",
};

export const AVISO_LEGAL =
    "Este resumen es orientativo y se redactó para ayudarte a capturar el concepto, no " +
    "sustituye a la ley ni a tu contador. El texto vigente es el de los PDF oficiales de la " +
    "Cámara de Diputados, que se ligan en cada bloque. Las leyes se reforman: si el importe " +
    "es grande o el caso es dudoso, verifícalo antes de timbrar.";

export const TIPO_LABEL = { 1: "Percepción", 2: "Deducción", 3: "Otro Pago" };

// Cómo se pinta la etiqueta de tratamiento fiscal en la ficha y en la lista.
export const TRATAMIENTO = {
    gravado: { texto: "Gravado de ISR", color: "red" },
    parcial: { texto: "Parcialmente exento", color: "orange" },
    exento: { texto: "Exento de ISR", color: "green" },
    deduccion: { texto: "Deducción", color: "blue" },
    na: { texto: "No es ingreso", color: "default" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Fichas completas: los conceptos que la gente captura de verdad.
// ─────────────────────────────────────────────────────────────────────────────
const FICHAS = [
    // ── Percepciones ─────────────────────────────────────────────────────────
    {
        clave: "001",
        tipo: 1,
        nombre: "Sueldos, Salarios, Rayas y Jornales",
        alias: ["sueldo", "salario", "raya", "jornal", "sueldos y salarios", "septimo dia", "vacaciones"],
        tratamientoIsr: "gravado",
        resumen: "Gravado al 100% de ISR e integra al salario base de cotización.",
        lft: {
            articulos: "Arts. 82, 84 y 69",
            texto:
                "El salario es la retribución que el patrón paga al trabajador por su trabajo " +
                "(Art. 82). El salario que se toma para indemnizaciones se integra con los pagos " +
                "en efectivo por cuota diaria, gratificaciones, percepciones, habitación, primas, " +
                "comisiones, prestaciones en especie y cualquier otra cantidad que se entregue al " +
                "trabajador por su trabajo (Art. 84). Por cada seis días de trabajo corresponde " +
                "un día de descanso con goce de salario íntegro: el séptimo día (Art. 69).",
        },
        isr: {
            articulos: "Art. 94",
            texto:
                "Es el ingreso por la prestación de un servicio personal subordinado. No tiene " +
                "exención: se grava el total.",
        },
        imss: {
            articulos: "Art. 27",
            texto: "Integra completo al salario base de cotización.",
        },
        captura: {
            banderas: ["esSueldo", "esSeptimoDia"],
            notas: [
                "El asistente de configuración crea tres conceptos con esta misma clave SAT: " +
                    "Sueldo, Séptimo Día y Vacaciones a tiempo.",
                "esSueldo y esSeptimoDia son marcas únicas por empresa: sólo un concepto de la " +
                    "empresa puede traer cada una.",
            ],
        },
        formulas: [
            {
                slot: "total", nombre: "Sueldo",
                formula:
                    "IIF(ES_VACACIONES, 0,\n  (DIAS_SUELDO_ANT * SALARIO_DIARIO_ANT) + (DIAS_SUELDO_VIG * SALARIO_DIARIO_VIG) - IMPORTE_INCIDENCIAS\n) + (DIAS_DESCANSO_VAC_PERIODO_COMPLETO * SALARIO_DIARIO_VIG)",
                explicacion:
                    "Paga los días del periodo al salario que corresponda, partido en dos por si " +
                    "hubo aumento a media quincena, y resta el importe de las incidencias. " +
                    "ES_VACACIONES y DIAS_DESCANSO_VAC_PERIODO_COMPLETO están reservadas para el " +
                    "pago adelantado de vacaciones, que todavía no se calcula: hoy valen 0 y esa " +
                    "parte de la fórmula no hace nada.",
            },
            { slot: "1", nombre: "Gravado ISR", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "2", nombre: "Exento ISR", formula: "0" },
            { slot: "3", nombre: "Gravado IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "4", nombre: "Exento IMSS", formula: "0" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones", "PTU Ingresos acumulados para"],
            importe1: ["ISR Base Gravada"],
            importe2: ["ISR Base Exenta"],
        },
        implementado: true,
    },
    {
        clave: "002",
        tipo: 1,
        nombre: "Gratificación Anual (Aguinaldo)",
        alias: ["aguinaldo", "gratificacion", "gratificacion anual", "bono de fin de año", "grati"],
        tratamientoIsr: "parcial",
        resumen: "Exento hasta 30 UMA al año; el excedente es gravado. Integra al SBC.",
        lft: {
            articulos: "Art. 87",
            texto:
                "Los trabajadores tienen derecho a un aguinaldo anual que debe pagarse antes del " +
                "20 de diciembre, equivalente a quince días de salario por lo menos. Quienes no " +
                "hayan cumplido el año de servicios tienen derecho a la parte proporcional al " +
                "tiempo trabajado, independientemente de que se encuentren laborando o no en la " +
                "fecha de liquidación del aguinaldo.",
        },
        isr: {
            articulos: "Art. 93, fracción XIV",
            texto:
                "Las gratificaciones que reciban los trabajadores durante un año de calendario " +
                "están exentas hasta el equivalente de la UMA elevada a 30 días, siempre que se " +
                "otorguen en forma general. El excedente es gravado.",
        },
        imss: {
            articulos: "Art. 27",
            texto: "Integra al salario base de cotización.",
        },
        captura: {
            banderas: ["esAguinaldo", "esPercepcionArt174"],
            notas: [
                "Marca Percepción Art. 174: su parte gravada sale de la base ordinaria del " +
                    "periodo y se retiene con la tasa efectiva del Art. 174 RLISR, en el concepto " +
                    "de deducción 'I.S.R. Art. 174'. Sin eso, la tarifa del periodo proyectaría el " +
                    "aguinaldo como si fuera sueldo mensual y dispararía la retención.",
                "esAguinaldo es una marca única por empresa: sólo un concepto la puede traer.",
                "Los días se capturan en la columna del concepto en la prenómina (VALOR), para " +
                    "que el mismo concepto sirva para 15 días o para la parte proporcional.",
            ],
        },
        formulas: [
            { slot: "total", nombre: "Aguinaldo", formula: "SALARIO_PRESTACIONES * VALOR" },
            {
                slot: "1", nombre: "Gravado ISR",
                formula:
                    "TOTAL_PERCEPCION_ACTUAL - MIN(TOTAL_PERCEPCION_ACTUAL, MAX(30 * UMA_DIARIA - ACUMULADO_ANIO_GRATIFICACION_EXENTA, 0))",
                explicacion:
                    "Exenta hasta 30 UMA, descontando lo que ya se exentó de aguinaldo antes en el " +
                    "mismo ejercicio. Por eso no basta con restar 30 UMA a secas: la exención es " +
                    "anual, no por pago.",
            },
            { slot: "2", nombre: "Exento ISR", formula: "TOTAL_PERCEPCION_ACTUAL - IMPORTE1_ACTUAL" },
            { slot: "3", nombre: "Gravado IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "4", nombre: "Exento IMSS", formula: "0" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones"],
            importe1: ["ISR Base Gravada Art142"],
            importe2: ["ISR Gratificación exenta"],
        },
        avisos: [
            {
                tipo: "warning",
                texto:
                    "El acumulado 'ISR Gratificación exenta' en el Importe 2 no es opcional: es " +
                    "lo que llena ACUMULADO_ANIO_GRATIFICACION_EXENTA. Sin él, la exención de 30 " +
                    "UMA se vuelve a otorgar completa en cada pago de aguinaldo del año.",
            },
        ],
        implementado: true,
    },
    {
        clave: "003",
        tipo: 1,
        nombre: "Participación de los Trabajadores en las Utilidades PTU",
        alias: ["ptu", "utilidades", "reparto de utilidades", "participacion de los trabajadores"],
        tratamientoIsr: "parcial",
        resumen: "Exenta hasta 15 UMA al año. No integra al salario base de cotización.",
        lft: {
            articulos: "Arts. 117 a 131",
            texto:
                "Los trabajadores participan en las utilidades de las empresas. El reparto se " +
                "hace en dos partes iguales: la primera se divide entre los días trabajados y la " +
                "segunda en proporción al salario devengado (Art. 123). El monto de la " +
                "participación tiene como límite tres meses del salario del trabajador o el " +
                "promedio de la participación recibida en los últimos tres años, aplicando el " +
                "que resulte más favorable (Art. 127, fracción VIII).",
        },
        isr: {
            articulos: "Art. 93, fracción XIV",
            texto:
                "La participación de los trabajadores en las utilidades está exenta hasta por el " +
                "equivalente a 15 días de UMA.",
        },
        imss: {
            articulos: "Art. 27, fracción IV",
            texto: "No integra al salario base de cotización.",
        },
        captura: {
            banderas: ["esPercepcionArt174"],
            notas: [
                "Sin fórmula de Importe Total: el reparto depende de la utilidad repartible de " +
                    "la empresa, así que el importe se captura por empleado en la prenómina.",
                "Marca Percepción Art. 174, igual que el aguinaldo.",
            ],
        },
        formulas: [
            {
                slot: "total", nombre: "PTU",
                formula: "0",
                explicacion: "Se captura a mano por empleado en la prenómina.",
            },
            {
                slot: "1", nombre: "Gravado ISR",
                formula:
                    "TOTAL_PERCEPCION_ACTUAL - MIN(TOTAL_PERCEPCION_ACTUAL, MAX(15 * UMA_DIARIA - ACUMULADO_ANIO_PTU_EXENTA, 0))",
            },
            { slot: "2", nombre: "Exento ISR", formula: "TOTAL_PERCEPCION_ACTUAL - IMPORTE1_ACTUAL" },
            {
                slot: "3", nombre: "Gravado IMSS",
                formula: "0",
                explicacion: "El Art. 27-IV LSS excluye la PTU del salario base de cotización.",
            },
            { slot: "4", nombre: "Exento IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones"],
            importe1: ["ISR Base Gravada Art142"],
            importe2: ["PTU ISR exento"],
        },
        implementado: true,
    },
    {
        clave: "005",
        tipo: 1,
        nombre: "Fondo de Ahorro",
        alias: ["fondo de ahorro", "ahorro"],
        tratamientoIsr: "parcial",
        resumen:
            "Prestación de previsión social: exenta si cumple los requisitos de generalidad y " +
            "los topes de previsión social. Biwo no la trae sembrada.",
        lft: {
            articulos: "Art. 110, fracción IV",
            texto:
                "La LFT no obliga a otorgar fondo de ahorro: es una prestación contractual. Lo " +
                "que sí regula es el descuento al salario para constituir cajas de ahorro, que " +
                "requiere el consentimiento del trabajador y no puede ser mayor al 30% del " +
                "excedente del salario mínimo.",
        },
        isr: {
            articulos: "Art. 93 y su penúltimo párrafo",
            texto:
                "La aportación del patrón al fondo de ahorro es previsión social y está exenta " +
                "cuando se otorga de forma general y el plan cumple los requisitos de " +
                "deducibilidad. Aplica además el límite general de previsión social: si el " +
                "salario del trabajador más las prestaciones de previsión social exceden 7 UMA " +
                "anuales, la parte exenta se limita a 1 UMA anual, sin que el resultado sea " +
                "menor a 7 UMA.",
        },
        imss: {
            articulos: "Art. 27, fracción II",
            texto:
                "No integra al salario base de cotización cuando el trabajador puede retirarlo " +
                "como máximo dos veces al año y la aportación patronal es igual a la del " +
                "trabajador.",
        },
        captura: {
            notas: [
                "El asistente de configuración no crea este concepto. Hay que darlo de alta a " +
                    "mano con tipo Percepción y clave SAT 001-005.",
                "La aportación del trabajador se captura aparte, como deducción.",
            ],
        },
        avisos: [
            {
                tipo: "warning",
                texto:
                    "El tope de previsión social se calcula sobre el ejercicio completo y depende " +
                    "de todas las prestaciones del trabajador, no sólo del fondo de ahorro. Biwo " +
                    "no lo resuelve solo: si tu plan se acerca al límite, calcula la parte exenta " +
                    "con tu contador antes de escribir la fórmula.",
            },
        ],
        implementado: false,
    },
    {
        clave: "010",
        tipo: 1,
        nombre: "Premios por puntualidad",
        alias: ["premio de puntualidad", "puntualidad", "bono de puntualidad"],
        tratamientoIsr: "gravado",
        resumen: "Gravado al 100%. Integra al SBC hasta el 10% del salario base.",
        lft: {
            articulos: "Art. 84",
            texto:
                "No es una prestación obligatoria. Cuando se otorga, forma parte del salario " +
                "integrado, porque es una cantidad que se entrega al trabajador por su trabajo.",
        },
        isr: { articulos: "Art. 94", texto: "No tiene exención: se grava el total." },
        imss: {
            articulos: "Art. 27, fracción VII",
            texto:
                "Los premios por asistencia y puntualidad no integran al salario base de " +
                "cotización siempre que su importe no rebase el 10% del salario base. Lo que " +
                "exceda ese 10% sí integra.",
        },
        captura: {
            notas: [
                "Es un buen caso para la función IIF: pagarlo sólo si el empleado no tuvo " +
                    "ausencias en el periodo.",
            ],
        },
        formulas: [
            {
                slot: "total", nombre: "Premio de puntualidad",
                formula: "IIF(TIENE_INCIDENCIA('RET', 'FINJ'), 0, 500)",
                explicacion:
                    "Propuesta: paga 500 sólo si no hubo retardos ni faltas injustificadas. " +
                    "Ajusta el importe y las claves a tu reglamento.",
            },
            { slot: "1", nombre: "Gravado ISR", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "2", nombre: "Exento ISR", formula: "0" },
        ],
        implementado: false,
    },
    {
        clave: "014",
        tipo: 1,
        nombre: "Subsidios por incapacidad",
        alias: ["subsidio por incapacidad", "incapacidad", "complemento de incapacidad"],
        tratamientoIsr: "parcial",
        resumen:
            "El subsidio que paga el IMSS no es ingreso del patrón. Lo que el patrón complementa " +
            "es previsión social sujeta a los topes.",
        lft: {
            articulos: "Art. 42, fracción II",
            texto:
                "La incapacidad temporal por enfermedad o riesgo de trabajo suspende la relación " +
                "laboral sin responsabilidad para las partes. El pago del subsidio corre a cargo " +
                "del IMSS; el patrón sólo cubre lo que su contrato le obligue a complementar.",
        },
        isr: {
            articulos: "Art. 93, fracción VI",
            texto:
                "Es prestación de previsión social. Está exenta cuando se otorga de forma " +
                "general, sujeta al límite general de previsión social.",
        },
        imss: {
            articulos: "Art. 27",
            texto:
                "Los días de incapacidad no cotizan para enfermedad y maternidad ni para riesgos " +
                "de trabajo, pero sí siguen cotizando para invalidez y vida.",
        },
        captura: {
            notas: [
                "En Biwo las incapacidades se capturan en el módulo de Incapacidades del " +
                    "empleado, no como movimiento del concepto.",
            ],
        },
        implementado: false,
    },
    {
        clave: "019",
        tipo: 1,
        nombre: "Horas extra",
        alias: ["horas extra", "tiempo extra", "extras", "horas extras", "he"],
        tratamientoIsr: "parcial",
        resumen:
            "Exento el 50% sin exceder 5 UMA por semana; 100% exento para quien gana el salario " +
            "mínimo. Biwo no trae la exención sembrada.",
        lft: {
            articulos: "Arts. 66, 67 y 68",
            texto:
                "La jornada puede prolongarse por circunstancias extraordinarias, sin exceder de " +
                "tres horas diarias ni de tres veces en una semana (Art. 66). Ese tiempo " +
                "extraordinario se paga con un 100% más del salario que corresponda a las horas " +
                "de la jornada, o sea al doble (Art. 67). La prolongación que exceda de nueve " +
                "horas a la semana obliga al patrón a pagar el tiempo excedente con un 200% más, " +
                "o sea al triple (Art. 68).",
        },
        isr: {
            articulos: "Art. 93, fracciones I y IV",
            texto:
                "Para los trabajadores del salario mínimo, las remuneraciones por tiempo " +
                "extraordinario están exentas hasta el límite de la legislación laboral. Para " +
                "los demás trabajadores está exento el 50% de la remuneración por tiempo " +
                "extraordinario, sin que la exención exceda de 5 UMA por cada semana de " +
                "servicios. Lo que exceda el máximo legal de horas se grava completo.",
        },
        imss: {
            articulos: "Art. 27, fracción IX",
            texto:
                "El tiempo extraordinario dentro de los márgenes de la LFT no integra al salario " +
                "base de cotización. El que excede esos márgenes sí integra.",
        },
        captura: {
            banderas: ["esHorasExtra"],
            notas: [
                "Las horas se capturan como incidencias con un Tipo de Incidencia marcado como " +
                    "horas extra simples, dobles o triples ante el SAT.",
                "El asistente de configuración no crea este concepto: hay que darlo de alta a mano.",
            ],
        },
        formulas: [
            {
                slot: "total", nombre: "Horas extra",
                formula: "(HORAS_EXTRA_DOBLES * SALARIO_X_HORA_VIG * 2) + (HORAS_EXTRA_TRIPLES * SALARIO_X_HORA_VIG * 3)",
                explicacion:
                    "Propuesta. Las horas llegan de las incidencias del periodo, ya separadas por " +
                    "el tipo que tengan marcado ante el SAT.",
            },
        ],
        avisos: [
            {
                tipo: "error",
                texto:
                    "Ojo: si marcas 'Es Horas Extra', el timbrado reinterpreta los Importes 1, 2 " +
                    "y 3 como horas Simples, Dobles y Triples para armar el nodo SAT HorasExtra, " +
                    "NO como Gravado ISR / Exento ISR / Gravado IMSS. En ese concepto no queda " +
                    "lugar para partir gravado y exento con la convención normal, y el motor va a " +
                    "tomar el Importe 1 (Simples) como base gravada de ISR. Si necesitas la " +
                    "exención del 50%, hoy hay que resolverla capturando los importes a mano o " +
                    "con dos conceptos separados. Consúltalo antes de timbrar.",
            },
        ],
        implementado: false,
    },
    {
        clave: "020",
        tipo: 1,
        nombre: "Prima dominical",
        alias: ["prima dominical", "dominical", "domingo", "domingos"],
        tratamientoIsr: "parcial",
        resumen: "Exenta 1 UMA por cada domingo laborado. Integra al SBC.",
        lft: {
            articulos: "Art. 71",
            texto:
                "En los reglamentos de esta Ley se procurará que el día de descanso semanal sea " +
                "el domingo. Los trabajadores que presten servicio en día domingo tendrán " +
                "derecho a una prima adicional de un veinticinco por ciento, por lo menos, sobre " +
                "el salario de los días ordinarios de trabajo.",
        },
        isr: {
            articulos: "Art. 93, fracción XIV",
            texto:
                "Tratándose de primas dominicales, la exención es de hasta el equivalente de una " +
                "UMA por cada domingo que se labore.",
        },
        imss: { articulos: "Art. 27", texto: "Integra al salario base de cotización." },
        captura: {
            banderas: ["esPercepcionArt174"],
            notas: [
                "Los domingos se toman de la columna VALOR del concepto y, si viene vacía, de " +
                    "las incidencias con Tipo de Incidencia marcado como prima dominical.",
                "El orden importa: la exención se resuelve contra el mismo dato que alimenta el " +
                    "total, para que capturar la prima a mano no deje la exención en cero.",
            ],
        },
        formulas: [
            {
                slot: "total", nombre: "PRIMA DOMINICAL",
                formula: "IIF(VALOR > 0, VALOR, DIAS_PRIMA_DOM) * SALARIO_DIARIO * 0.25",
            },
            {
                slot: "1", nombre: "Gravado ISR",
                formula: "MAX(TOTAL_PERCEPCION_ACTUAL - IIF(VALOR > 0, VALOR, DIAS_PRIMA_DOM) * UMA_DIARIA, 0)",
                explicacion:
                    "Con salario diario por debajo de 4 UMA la prima (salario × 25%) nunca alcanza " +
                    "la exención de 1 UMA, así que el pago queda 100% exento.",
            },
            { slot: "2", nombre: "Exento ISR", formula: "TOTAL_PERCEPCION_ACTUAL - IMPORTE1_ACTUAL" },
            { slot: "3", nombre: "Gravado IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "4", nombre: "Exento IMSS", formula: "0" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones"],
            importe1: ["ISR Base Gravada Art142"],
        },
        implementado: true,
    },
    {
        clave: "021",
        tipo: 1,
        nombre: "Prima vacacional",
        alias: ["prima vacacional", "prima de vacaciones", "vacacional"],
        tratamientoIsr: "parcial",
        resumen: "Exenta hasta 15 UMA al año. Integra al SBC.",
        lft: {
            articulos: "Arts. 76 y 80",
            texto:
                "Los trabajadores con más de un año de servicios tienen derecho a un periodo " +
                "anual de vacaciones pagadas, que en ningún caso podrá ser inferior a doce días " +
                "laborables, y que aumenta en dos días hasta llegar a veinte; a partir del sexto " +
                "año aumenta en dos días por cada cinco de servicios (Art. 76, reforma vigente " +
                "desde 2023). Los trabajadores tienen derecho a una prima no menor de " +
                "veinticinco por ciento sobre los salarios que les correspondan durante el " +
                "periodo de vacaciones (Art. 80).",
        },
        isr: {
            articulos: "Art. 93, fracción XIV",
            texto:
                "Las primas vacacionales que otorguen los patrones durante el año de calendario " +
                "en forma general están exentas hasta por el equivalente a 15 días de UMA.",
        },
        imss: { articulos: "Art. 27", texto: "Integra al salario base de cotización." },
        captura: {
            banderas: ["esPrimaVacacional", "esPercepcionArt174"],
            notas: [
                "Los días llegan del módulo de Vacaciones (PRIMA_VACACIONAL_DIAS) y, en un " +
                    "finiquito, de lo capturado en VALOR. Se suman porque un finiquito puede caer " +
                    "en un periodo donde el empleado además tomó vacaciones.",
                "Las vacaciones en sí NO tienen exención: sólo su prima. Van en el concepto de " +
                    "clave 001.",
            ],
        },
        formulas: [
            {
                slot: "total", nombre: "PRIMA VACACIONAL",
                formula: "SALARIO_PRESTACIONES * (PRIMA_VACACIONAL_DIAS + VALOR)",
            },
            {
                slot: "1", nombre: "Gravado ISR",
                formula:
                    "TOTAL_PERCEPCION_ACTUAL - MIN(TOTAL_PERCEPCION_ACTUAL, MAX(15 * UMA_DIARIA - ACUMULADO_ANIO_PRIMA_VAC_EXENTA, 0))",
            },
            { slot: "2", nombre: "Exento ISR", formula: "TOTAL_PERCEPCION_ACTUAL - IMPORTE1_ACTUAL" },
            { slot: "3", nombre: "Gravado IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "4", nombre: "Exento IMSS", formula: "0" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones"],
            importe1: ["ISR Base Gravada Art142"],
            importe2: ["ISR Prima vac. exenta"],
        },
        implementado: true,
    },
    {
        clave: "022",
        tipo: 1,
        nombre: "Prima por antigüedad",
        alias: ["prima de antiguedad", "antiguedad", "prima por antiguedad"],
        tratamientoIsr: "parcial",
        resumen:
            "12 días por año, con el salario topado a 2 salarios mínimos. Exenta dentro de la " +
            "bolsa de 90 UMA por año de servicio.",
        lft: {
            articulos: "Arts. 162 y 486",
            texto:
                "Los trabajadores de planta tienen derecho a una prima de antigüedad de doce " +
                "días de salario por cada año de servicios (Art. 162, fracción I). Para " +
                "determinarla, si el salario excede del doble del salario mínimo, se considera " +
                "esa cantidad como salario máximo (Arts. 162-II y 486).",
        },
        isr: {
            articulos: "Art. 93, fracción XIII",
            texto:
                "Los ingresos por prima de antigüedad, retiro e indemnizaciones que obtengan las " +
                "personas al momento de su separación están exentos hasta por el equivalente a " +
                "noventa veces la UMA por cada año de servicio.",
        },
        imss: {
            articulos: "Art. 84 LFT",
            texto: "No integra al salario base de cotización: no es parte del salario.",
        },
        captura: {
            banderas: ["esPrimaAntiguedadSeparacion"],
            notas: [
                "El tope de 2 salarios mínimos vive dentro de la fórmula, no en el controlador, " +
                    "para que se recalcule solo si cambia el mínimo.",
                "Los días se capturan por MovimientoPermanente desde el módulo de Finiquito.",
            ],
        },
        formulas: [
            {
                slot: "total", nombre: "Prima de antigüedad",
                formula: "MIN(2 * SALARIO_MINIMO_DIARIO, SALARIO_BASE_FINIQUITO) * VALOR",
            },
            {
                slot: "1", nombre: "Gravado ISR",
                formula:
                    "TOTAL_PERCEPCION_ACTUAL - MIN(TOTAL_PERCEPCION_ACTUAL, MAX(90 * UMA_DIARIA * ANIOS_SERVICIO_FINIQUITO - PERIODO_LIQUIDACION_EXENTO, 0))",
            },
            { slot: "2", nombre: "Exento ISR", formula: "TOTAL_PERCEPCION_ACTUAL - IMPORTE1_ACTUAL" },
            { slot: "3", nombre: "Gravado IMSS", formula: "0" },
            { slot: "4", nombre: "Exento IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones"],
            importe1: ["ISR Liquidacion gravado"],
            importe2: ["ISR Liquidacion exento"],
        },
        bolsaSeparacion: true,
        implementado: true,
    },
    {
        clave: "023",
        tipo: 1,
        nombre: "Pagos por separación",
        alias: ["separacion", "separacion unica", "20 dias por año", "pagos por separacion"],
        tratamientoIsr: "parcial",
        resumen:
            "Los 20 días por año del Art. 50-II LFT. Exentos dentro de la bolsa de 90 UMA por " +
            "año de servicio.",
        lft: {
            articulos: "Art. 50, fracción II",
            texto:
                "Cuando la relación de trabajo por tiempo indeterminado se rescinde, la " +
                "indemnización consiste en veinte días de salario por cada uno de los años de " +
                "servicios prestados.",
        },
        isr: {
            articulos: "Art. 93, fracción XIII",
            texto:
                "Exentos hasta por el equivalente a noventa veces la UMA por cada año de " +
                "servicio, en bolsa común con la indemnización y la prima de antigüedad.",
        },
        imss: {
            articulos: "Art. 84 LFT",
            texto: "No integra al salario base de cotización.",
        },
        captura: {
            banderas: ["esIndemnizacionSeparacion"],
            notas: [
                "Su ISR se retiene con el procedimiento del Art. 96 último párrafo LISR (tasa " +
                    "del último sueldo mensual ordinario), en el concepto 'I.S.R. finiquito'. Por " +
                    "eso NO lleva Percepción Art. 174.",
            ],
        },
        formulas: [
            { slot: "total", nombre: "Separación Única", formula: "SALARIO_BASE_FINIQUITO * VALOR" },
            {
                slot: "1", nombre: "Gravado ISR",
                formula:
                    "TOTAL_PERCEPCION_ACTUAL - MIN(TOTAL_PERCEPCION_ACTUAL, MAX(90 * UMA_DIARIA * ANIOS_SERVICIO_FINIQUITO - PERIODO_LIQUIDACION_EXENTO, 0))",
            },
            { slot: "2", nombre: "Exento ISR", formula: "TOTAL_PERCEPCION_ACTUAL - IMPORTE1_ACTUAL" },
            { slot: "3", nombre: "Gravado IMSS", formula: "0" },
            { slot: "4", nombre: "Exento IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones"],
            importe1: ["ISR Liquidacion gravado"],
            importe2: ["ISR Liquidacion exento"],
        },
        bolsaSeparacion: true,
        implementado: true,
    },
    {
        clave: "025",
        tipo: 1,
        nombre: "Indemnizaciones",
        alias: ["indemnizacion", "indemnizaciones", "3 meses", "indemnizacion constitucional", "liquidacion"],
        tratamientoIsr: "parcial",
        resumen:
            "La indemnización constitucional de 90 días. Exenta dentro de la bolsa de 90 UMA " +
            "por año de servicio.",
        lft: {
            articulos: "Arts. 48 y 50, fracción III",
            texto:
                "El trabajador despedido sin causa justificada puede pedir ante el Tribunal que " +
                "se le reinstale o que se le indemnice con el importe de tres meses de salario " +
                "(Art. 48). La indemnización consistirá además en el importe de tres meses de " +
                "salario (Art. 50, fracción III).",
        },
        isr: {
            articulos: "Art. 93, fracción XIII",
            texto:
                "Exenta hasta por el equivalente a noventa veces la UMA por cada año de " +
                "servicio, en bolsa común con la separación y la prima de antigüedad.",
        },
        imss: {
            articulos: "Art. 84 LFT",
            texto: "No integra al salario base de cotización.",
        },
        captura: {
            banderas: ["esIndemnizacionSeparacion"],
            notas: [
                "Los días son capturables porque casi siempre se negocian: la pantalla de " +
                    "finiquito propone 90 y el usuario baja el número a lo convenido.",
                "Su ISR va por el concepto 'I.S.R. finiquito', no por el Art. 174.",
            ],
        },
        formulas: [
            { slot: "total", nombre: "Indemnización", formula: "SALARIO_BASE_FINIQUITO * VALOR" },
            {
                slot: "1", nombre: "Gravado ISR",
                formula:
                    "TOTAL_PERCEPCION_ACTUAL - MIN(TOTAL_PERCEPCION_ACTUAL, MAX(90 * UMA_DIARIA * ANIOS_SERVICIO_FINIQUITO - PERIODO_LIQUIDACION_EXENTO, 0))",
            },
            { slot: "2", nombre: "Exento ISR", formula: "TOTAL_PERCEPCION_ACTUAL - IMPORTE1_ACTUAL" },
            { slot: "3", nombre: "Gravado IMSS", formula: "0" },
            { slot: "4", nombre: "Exento IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones"],
            importe1: ["ISR Liquidacion gravado"],
            importe2: ["ISR Liquidacion exento"],
        },
        bolsaSeparacion: true,
        implementado: true,
    },
    {
        clave: "028",
        tipo: 1,
        nombre: "Comisiones",
        alias: ["comision", "comisiones", "comisionista"],
        tratamientoIsr: "gravado",
        resumen: "Gravadas al 100%. Integran al salario base de cotización como parte variable.",
        lft: {
            articulos: "Arts. 84, 285 y 286",
            texto:
                "Las comisiones forman parte del salario (Art. 84). Los agentes de comercio y " +
                "demás trabajadores semejantes son trabajadores de la empresa a la que prestan " +
                "sus servicios (Art. 285); su salario puede fijarse por comisión, y para efectos " +
                "de salario se toma el promedio que resulte de los ingresos de todo el año o de " +
                "los del tiempo trabajado si fue menor (Art. 286).",
        },
        isr: { articulos: "Art. 94", texto: "No tienen exención: se grava el total." },
        imss: {
            articulos: "Art. 27 y Art. 30, fracción II",
            texto:
                "Integran al salario base de cotización como parte variable. El SBC variable se " +
                "recalcula bimestralmente con el promedio de lo percibido en el bimestre anterior.",
        },
        captura: {
            notas: [
                "El importe suele capturarse por empleado en la prenómina, o calcularse con una " +
                    "fórmula sobre otro concepto ya calculado (por ejemplo P001 * 0.05).",
            ],
        },
        formulas: [
            { slot: "1", nombre: "Gravado ISR", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "2", nombre: "Exento ISR", formula: "0" },
            { slot: "3", nombre: "Gravado IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "4", nombre: "Exento IMSS", formula: "0" },
        ],
        implementado: false,
    },
    {
        clave: "029",
        tipo: 1,
        nombre: "Vales de despensa",
        alias: ["vales", "vales de despensa", "despensa", "monedero"],
        tratamientoIsr: "parcial",
        resumen:
            "Previsión social: exentos si se otorgan de forma general, sujetos al tope de " +
            "previsión social. Para el IMSS no integran hasta el 40% de la UMA.",
        lft: {
            articulos: "Art. 84",
            texto:
                "No es una prestación obligatoria de la LFT. Cuando se otorga en dinero o en " +
                "especie por el trabajo, forma parte del salario integrado para indemnizaciones.",
        },
        isr: {
            articulos: "Art. 93 y su penúltimo párrafo; Art. 27, fracción XI",
            texto:
                "Son previsión social y están exentos cuando se otorgan de forma general, " +
                "sujetos al límite general de previsión social (7 UMA anuales de salario más " +
                "previsión social; si se excede, la exención se limita a 1 UMA anual sin quedar " +
                "por debajo de 7 UMA). Para que el patrón pueda deducirlos deben entregarse en " +
                "monedero electrónico autorizado por el SAT.",
        },
        imss: {
            articulos: "Art. 27, fracción VI",
            texto:
                "La despensa en especie o en dinero no integra al salario base de cotización " +
                "hasta el equivalente al 40% de la UMA. Lo que exceda ese 40% sí integra.",
        },
        captura: {
            notas: [
                "El asistente de configuración lo crea como «Despensa», con la casilla Especie " +
                    "prendida y sin Automático Global: hay que asignárselo a quien lo recibe y " +
                    "capturarle el importe por movimiento permanente o a mano.",
                "Va con Especie porque el monedero se carga aparte: el importe se timbra en el " +
                    "CFDI y grava, pero no sube el neto a pagar ni sale en la dispersión bancaria. " +
                    "Si tu empresa lo paga en efectivo dentro de la nómina, apaga esa casilla.",
                "El asistente lo siembra también con «No contabilizar», porque el gasto ya entró " +
                    "con la factura del proveedor de los vales. Si la despensa la produce la propia " +
                    "empresa, apaga esa bandera y captúrale cuentas.",
                "El asistente deja el ISR al 100% gravado a propósito: la exención de previsión " +
                    "social es anual y depende de todas las prestaciones del trabajador, así que " +
                    "no hay fórmula por periodo que sirva para todos. Ajusta los Importes 1 y 2 " +
                    "con tu contador — gravar de más se corrige, gravar de menos lo cobra el SAT.",
                "Los topes de ISR y de IMSS son distintos y se calculan sobre bases distintas: " +
                    "el de ISR es anual y de previsión social, el del IMSS es del 40% de la UMA. " +
                    "Por eso el Importe 2 (exento ISR) y el Importe 4 (exento IMSS) no coinciden.",
            ],
        },
        formulas: [
            {
                slot: "3", nombre: "Gravado IMSS",
                formula: "MAX(TOTAL_PERCEPCION_ACTUAL - (0.40 * UMA_DIARIA * DIAS_PAGO), 0)",
                explicacion:
                    "Propuesta para el Art. 27-VI LSS: sólo integra lo que pase del 40% de la UMA " +
                    "por los días del periodo.",
            },
            {
                slot: "4", nombre: "Exento IMSS",
                formula: "MIN(TOTAL_PERCEPCION_ACTUAL, 0.40 * UMA_DIARIA * DIAS_PAGO)",
                explicacion:
                    "Aquí hay que repetir el tope en vez de restar el importe anterior: el motor " +
                    "sólo expone IMPORTE1_ACTUAL (el gravado de ISR), no el Importe 3.",
            },
        ],
        avisos: [
            {
                tipo: "warning",
                texto:
                    "La exención de ISR de la previsión social se calcula sobre el ejercicio " +
                    "completo y depende de todas las prestaciones del trabajador, no sólo de los " +
                    "vales. Por eso aquí sólo se propone la parte de IMSS: la de ISR hay que " +
                    "calcularla con tu contador antes de escribir los Importes 1 y 2.",
            },
        ],
        implementado: true,
    },
    {
        clave: "038",
        tipo: 1,
        nombre: "Otros ingresos por salarios",
        alias: ["otros ingresos", "otros ingresos por salarios", "bono", "compensacion", "gratificacion extraordinaria"],
        tratamientoIsr: "gravado",
        resumen:
            "La clave de uso general para percepciones gravadas que no encajan en otra clave.",
        lft: {
            articulos: "Art. 84",
            texto:
                "Cualquier cantidad que se entregue al trabajador por su trabajo forma parte del " +
                "salario integrado.",
        },
        isr: {
            articulos: "Art. 94",
            texto: "Sin exención propia: se grava el total, salvo que califique como previsión social.",
        },
        imss: { articulos: "Art. 27", texto: "Integra al salario base de cotización." },
        captura: {
            notas: [
                "Úsala sólo cuando de verdad no exista una clave específica. Poner un aguinaldo " +
                    "o una prima vacacional aquí pierde su exención y su tratamiento del Art. 174.",
            ],
        },
        formulas: [
            { slot: "1", nombre: "Gravado ISR", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "2", nombre: "Exento ISR", formula: "0" },
            { slot: "3", nombre: "Gravado IMSS", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "4", nombre: "Exento IMSS", formula: "0" },
        ],
        implementado: false,
    },
    {
        clave: "046",
        tipo: 1,
        nombre: "Ingresos asimilados a salarios",
        alias: ["asimilados", "asimilado a salarios", "ingresos asimilados"],
        tratamientoIsr: "gravado",
        resumen:
            "No hay relación laboral: se grava el total y no hay cuotas al IMSS ni prestaciones " +
            "de la LFT.",
        lft: {
            articulos: "No aplica",
            texto:
                "Los asimilados a salarios no son trabajadores para la LFT: no hay relación " +
                "laboral, por lo que no les corresponden aguinaldo, vacaciones, prima vacacional " +
                "ni prima de antigüedad. Si en los hechos hay subordinación, la relación es " +
                "laboral aunque el contrato diga otra cosa.",
        },
        isr: {
            articulos: "Art. 94, fracciones I a VII",
            texto:
                "Se asimilan a salarios, entre otros, los honorarios a miembros de consejos, " +
                "administradores y comisarios; los honorarios a personas que presten servicios " +
                "preponderantemente a un prestatario; y los ingresos de personas físicas por " +
                "actividades empresariales que opten por asimilarlos. No tienen las exenciones " +
                "del Art. 93 que corresponden a los trabajadores.",
        },
        imss: {
            articulos: "No aplica",
            texto: "Sin relación laboral no hay obligación de inscribirlos al IMSS.",
        },
        captura: {
            notas: [
                "El régimen del empleado se captura en su ficha, en la pestaña Generales. Al " +
                    "elegir un régimen de asimilados (claves 05 a 11) el formulario le propone " +
                    "solo los conceptos que le tocan: esta percepción y la cadena de ISR, sin " +
                    "Sueldo, sin Séptimo Día y sin IMSS.",
                "El Importe Total va sin fórmula (en 0): el importe no sale de un salario " +
                    "diario. Se captura con un movimiento permanente, o a mano en el " +
                    "sobre-recibo cuando el pago es irregular.",
                "El subsidio al empleo no les aplica, y el motor ya lo respeta solo: varias " +
                    "fórmulas distinguen por TIPO_REGIMEN_EMPLEADO, donde el 2 es sueldos y " +
                    "salarios. El ISR en cambio se les retiene completo.",
                "Ojo con el tipo de contrato: el SAT solo acepta las claves 09, 10 y 99 junto " +
                    "a un régimen de asimilados. Con un contrato 01 a 08 el PAC rechaza el CFDI.",
            ],
            banderas: ["esIngresoAsimilados"],
        },
        formulas: [
            { slot: "total", nombre: "Ingreso asimilados a salarios", formula: "0" },
            { slot: "1", nombre: "ISR gravado", formula: "TOTAL_PERCEPCION_ACTUAL" },
            { slot: "2", nombre: "Exento ISR", formula: "0" },
            { slot: "3", nombre: "Gravado IMSS", formula: "0" },
            { slot: "4", nombre: "Exento IMSS", formula: "0" },
        ],
        acumulados: {
            total: ["ISR Total de percepciones"],
            importe1: ["ISR Base Gravada"],
        },
        implementado: true,
    },
    {
        clave: "050",
        tipo: 1,
        nombre: "Viáticos",
        alias: ["viaticos", "viatico", "gastos de viaje"],
        tratamientoIsr: "parcial",
        resumen:
            "Exentos cuando son efectivamente erogados en servicio del patrón y se comprueban " +
            "con CFDI. Lo no comprobado se grava.",
        lft: {
            articulos: "Art. 84",
            texto:
                "Los viáticos no son salario: son reembolso de gastos hechos por cuenta del " +
                "patrón. No forman parte del salario integrado mientras se comprueben.",
        },
        isr: {
            articulos: "Art. 93, fracción XVII",
            texto:
                "Están exentos los viáticos cuando sean efectivamente erogados en servicio del " +
                "patrón y se compruebe esta circunstancia con los comprobantes fiscales " +
                "correspondientes. Lo no comprobado se considera ingreso gravado.",
        },
        imss: {
            articulos: "Art. 27, fracción III",
            texto:
                "Los viáticos no integran al salario base de cotización cuando se comprueban con " +
                "documentación.",
        },
        captura: {
            notas: [
                "Ojo con la clave: la percepción 050 'Viáticos' declara el viático gravado; el " +
                    "viático entregado al trabajador y comprobado va por Otros Pagos 003.",
            ],
        },
        implementado: false,
    },

    // ── Deducciones ──────────────────────────────────────────────────────────
    {
        clave: "001",
        tipo: 2,
        nombre: "Seguridad social",
        alias: ["imss", "seguridad social", "cuota obrera", "retencion imss"],
        tratamientoIsr: "deduccion",
        resumen: "Las cuotas obrero-patronales que se le retienen al trabajador.",
        lft: {
            articulos: "Art. 110, fracción II",
            texto:
                "Es uno de los descuentos permitidos al salario: el pago de cuotas para la " +
                "constitución y fomento de sociedades cooperativas y cajas de ahorro, y las " +
                "aportaciones a instituciones de seguridad social.",
        },
        isr: {
            articulos: "Art. 93, fracción XII",
            texto:
                "La cuota de seguridad social de los trabajadores pagada por los patrones está " +
                "exenta para el trabajador. La que se le retiene a él es sólo una retención, no " +
                "un ingreso.",
        },
        imss: {
            articulos: "Arts. 25, 106, 107, 147 y 168 LSS",
            texto:
                "La cuota obrera se calcula sobre el salario base de cotización topado a 25 UMA " +
                "y se compone de enfermedad y maternidad, invalidez y vida, y cesantía en edad " +
                "avanzada y vejez.",
        },
        captura: {
            notas: [
                "El asistente crea el concepto 'I.M.S.S.' con la fórmula IMSS_OBRERO, que ya " +
                    "suma todos los ramos. Además crea conceptos informativos por ramo con No Suma " +
                    "prendido, para poder auditar el cálculo sin afectar el neto.",
            ],
        },
        formulas: [{ slot: "total", nombre: "I.M.S.S.", formula: "IMSS_OBRERO" }],
        implementado: true,
    },
    {
        clave: "002",
        tipo: 2,
        nombre: "ISR",
        alias: ["isr", "impuesto sobre la renta", "retencion isr", "impuesto"],
        tratamientoIsr: "deduccion",
        resumen: "La retención del impuesto sobre la renta del periodo.",
        lft: {
            articulos: "Art. 110, fracción I",
            texto:
                "El descuento por el pago de impuestos es uno de los que la ley permite hacer al " +
                "salario del trabajador.",
        },
        isr: {
            articulos: "Arts. 96 y 174 RLISR",
            texto:
                "Quienes hagan pagos por salarios están obligados a efectuar retenciones y " +
                "enteros mensuales, aplicando la tarifa del Art. 96. Para las gratificaciones, " +
                "PTU, primas dominicales y primas vacacionales, el Art. 174 del Reglamento " +
                "permite retener con la tasa efectiva que esos pagos provocan sobre el ingreso " +
                "ordinario mensual, en lugar de proyectarlos con la tarifa del periodo.",
        },
        captura: {
            notas: [
                "El asistente crea varios conceptos con esta clave: 'I.S.R. (mes)', 'I.S.R. Art. " +
                    "174' e 'I.S.R. finiquito'. Cada uno retiene una base distinta y no se " +
                    "sustituyen entre sí.",
                "El de finiquito usa el procedimiento del Art. 96 último párrafo: la tasa del " +
                    "último sueldo mensual ordinario, no la tarifa del periodo.",
            ],
        },
        formulas: [
            { slot: "total", nombre: "I.S.R. (mes)", formula: "ISR_MES + SUBSIDIO_ACREDITADO" },
            {
                slot: "1", nombre: "ISR retenido del periodo",
                formula: "IIF(TIPO_REGIMEN_EMPLEADO == 2, ISR_ANTES_SUBSIDIO_PERIODO, 0)",
                explicacion: "Informativo: el ISR retenido del periodo, sólo para sueldos y salarios.",
            },
        ],
        implementado: true,
    },
    {
        clave: "004",
        tipo: 2,
        nombre: "Otros",
        alias: ["otros", "otras deducciones", "prestamo", "caja de ahorro", "descuento"],
        tratamientoIsr: "deduccion",
        resumen: "La clave de uso general para descuentos que no tienen clave propia.",
        lft: {
            articulos: "Art. 110",
            texto:
                "Los descuentos al salario están limitados: sólo proceden los que la ley enumera " +
                "(deudas con el patrón, INFONAVIT, cuotas sindicales, pensión alimenticia, " +
                "cajas de ahorro, entre otros) y con los topes que cada fracción señala. La " +
                "deuda del trabajador con el patrón nunca puede exceder el importe de un mes de " +
                "salario, y el descuento no puede ser mayor al 30% del excedente del mínimo.",
        },
        isr: { articulos: "No aplica", texto: "Un descuento no es un ingreso: no grava ni exenta." },
        captura: {
            notas: [
                "El asistente la usa para 'Prestamo empresa', 'Caja de ahorro' y 'Ajuste al neto'.",
                "El importe se captura por empleado en la prenómina; la fórmula queda en 0.",
            ],
        },
        implementado: true,
    },
    {
        clave: "006",
        tipo: 2,
        nombre: "Descuento por incapacidad",
        alias: ["descuento por incapacidad", "incapacidad descuento"],
        tratamientoIsr: "deduccion",
        resumen: "Los días de incapacidad que no paga el patrón.",
        lft: {
            articulos: "Art. 42, fracción II",
            texto:
                "La incapacidad temporal suspende la relación de trabajo sin responsabilidad " +
                "para el trabajador ni para el patrón: durante esos días el patrón no está " +
                "obligado a pagar salario.",
        },
        isr: { articulos: "No aplica", texto: "Es un descuento, no un ingreso." },
        implementado: false,
    },
    {
        clave: "007",
        tipo: 2,
        nombre: "Pensión alimenticia",
        alias: ["pension alimenticia", "pension", "alimentos", "descuento judicial"],
        tratamientoIsr: "deduccion",
        resumen: "Descuento decretado por autoridad judicial. Es el único sin tope de la LFT.",
        lft: {
            articulos: "Art. 110, fracción V",
            texto:
                "Procede el descuento por pago de pensiones alimenticias decretadas por la " +
                "autoridad competente en favor de las personas señaladas en el Art. 110. Este " +
                "descuento no está sujeto a los límites porcentuales de las demás fracciones.",
        },
        isr: { articulos: "No aplica", texto: "Es un descuento, no un ingreso." },
        captura: {
            notas: [
                "El asistente lo crea con subcuenta contable por empleado, para poder seguir el " +
                    "saldo de cada caso por separado.",
            ],
        },
        implementado: true,
    },
    {
        clave: "009",
        tipo: 2,
        nombre: "Préstamos provenientes del Fondo Nacional de la Vivienda para los Trabajadores",
        alias: ["infonavit", "credito infonavit", "prestamo infonavit", "vivienda"],
        tratamientoIsr: "deduccion",
        resumen: "La amortización del crédito de vivienda que se le retiene al trabajador.",
        lft: {
            articulos: "Art. 110, fracción III",
            texto:
                "Procede el descuento por el pago de abonos para cubrir préstamos otorgados por " +
                "el INFONAVIT, destinados a la adquisición, construcción, reparación o mejora de " +
                "casas habitación. El descuento debe haber sido aceptado libremente por el " +
                "trabajador y no puede exceder del 20% del salario.",
        },
        isr: { articulos: "No aplica", texto: "Es un descuento, no un ingreso." },
        imss: {
            articulos: "Art. 29 Ley del INFONAVIT",
            texto:
                "La amortización se calcula sobre el salario base de cotización y el patrón debe " +
                "retenerla y enterarla bimestralmente junto con las cuotas del IMSS.",
        },
        captura: {
            banderas: ["esCreditoInfonvitCF", "esCreditoInfonvitFD", "esCreditoInfonvitPORC"],
            notas: [
                "Hay tres conceptos, uno por tipo de descuento: cuota fija (CF), factor de " +
                    "descuento (FD) y porcentaje (PORC). Cada bandera es única por empresa.",
                "En estos conceptos los Importes 1 a 4 se usan como subperiodos del bimestre, no " +
                    "como gravado y exento.",
            ],
        },
        implementado: true,
    },
    {
        clave: "011",
        tipo: 2,
        nombre: "Pago de abonos INFONACOT",
        alias: ["fonacot", "infonacot", "credito fonacot", "abonos fonacot"],
        tratamientoIsr: "deduccion",
        resumen: "La amortización del crédito FONACOT.",
        lft: {
            articulos: "Art. 103 Bis",
            texto:
                "El Instituto FONACOT otorga financiamiento a los trabajadores para la " +
                "adquisición de bienes y servicios. El patrón que reciba la notificación está " +
                "obligado a retener y enterar los descuentos; el trabajador debe haber aceptado " +
                "el crédito libremente.",
        },
        isr: { articulos: "No aplica", texto: "Es un descuento, no un ingreso." },
        captura: {
            banderas: ["esCreditoFonacot"],
            notas: [
                "Un crédito FONACOT sin fecha de aplicación capturada es invisible para el " +
                    "motor: no se descuenta y no avisa.",
            ],
        },
        formulas: [{ slot: "total", nombre: "Préstamo FONACOT", formula: "FONACOT_AMORTIZACION" }],
        implementado: true,
    },
    {
        clave: "019",
        tipo: 2,
        nombre: "Cuotas sindicales",
        alias: ["cuota sindical", "sindicato", "cuotas sindicales"],
        tratamientoIsr: "deduccion",
        resumen: "Descuento a favor del sindicato, previsto en el contrato colectivo.",
        lft: {
            articulos: "Art. 110, fracción VI",
            texto:
                "Procede el descuento por el pago de cuotas sindicales ordinarias previstas en " +
                "los estatutos del sindicato.",
        },
        isr: {
            articulos: "Art. 93",
            texto:
                "Cuando las paga el patrón por cuenta del trabajador son previsión social exenta " +
                "(percepción 013). Cuando se le retienen al trabajador son sólo un descuento.",
        },
        implementado: false,
    },
    {
        clave: "020",
        tipo: 2,
        nombre: "Ausencia (Ausentismo)",
        alias: ["falta", "faltas", "ausentismo", "ausencia", "descuento por faltas"],
        tratamientoIsr: "deduccion",
        resumen: "El descuento por los días no trabajados.",
        lft: {
            articulos: "Art. 82",
            texto:
                "El salario es la retribución por el trabajo prestado. Los días no laborados sin " +
                "justificación no generan salario, de modo que el descuento no es una sanción " +
                "económica prohibida sino la falta de causa del pago.",
        },
        isr: { articulos: "No aplica", texto: "Es un descuento, no un ingreso." },
        captura: {
            notas: [
                "En Biwo las faltas se capturan como incidencias y el concepto de Sueldo ya las " +
                    "resta en IMPORTE_INCIDENCIAS. Un concepto de deducción aparte duplicaría el " +
                    "descuento: úsalo sólo si tu concepto de sueldo no las descuenta.",
            ],
        },
        implementado: false,
    },

    // ── Otros Pagos ──────────────────────────────────────────────────────────
    {
        clave: "002",
        tipo: 3,
        nombre: "Subsidio para el empleo (efectivamente entregado al trabajador)",
        alias: ["subsidio", "subsidio al empleo", "subsidio para el empleo"],
        tratamientoIsr: "na",
        resumen:
            "No es ingreso: es un apoyo fiscal que el patrón entrega y luego acredita contra el " +
            "ISR que entera.",
        lft: {
            articulos: "No aplica",
            texto: "El subsidio al empleo es una figura fiscal, no una prestación laboral.",
        },
        isr: {
            articulos: "Decreto del subsidio para el empleo",
            texto:
                "Se calcula con la tabla del subsidio sobre los ingresos gravados del periodo. " +
                "Se resta del ISR a cargo; si el subsidio es mayor, la diferencia se entrega en " +
                "efectivo al trabajador y el patrón la acredita. No aplica a los asimilados a " +
                "salarios.",
        },
        captura: {
            notas: [
                "El asistente crea dos conceptos: 'Subs al Empleo acreditado' (lo que se " +
                    "descuenta del ISR) y 'Subs al Empleo (mes)' (lo que se entrega en efectivo).",
                "La variable SUBSIDIO_FINAL ya viene con signo negativo, porque va en la columna " +
                    "de deducciones y lo que hace es descontar menos.",
            ],
        },
        formulas: [
            {
                slot: "total", nombre: "Subs al Empleo (mes)",
                formula: "SUBSIDIO_MES + SUBSIDIO_ENTREGADO_DE_MENOS",
                explicacion:
                    "SUBSIDIO_MES hoy vale siempre 0: el ajuste de fin de mes se absorbió dentro " +
                    "de SUBSIDIO_ACREDITADO. En la práctica este concepto sólo trae el ajuste de " +
                    "lo entregado de menos.",
            },
        ],
        implementado: true,
    },
    {
        clave: "003",
        tipo: 3,
        nombre: "Viáticos (entregados al trabajador)",
        alias: ["viaticos entregados", "viaticos comprobados"],
        tratamientoIsr: "na",
        resumen:
            "El viático comprobado. Se declara aquí como informativo, sin sumar al ingreso " +
            "gravado.",
        lft: {
            articulos: "Art. 84",
            texto: "No es salario: es reembolso de gastos hechos por cuenta del patrón.",
        },
        isr: {
            articulos: "Art. 93, fracción XVII",
            texto:
                "Exentos cuando son efectivamente erogados en servicio del patrón y se comprueban " +
                "con CFDI. El SAT pide declararlos en el recibo aunque estén exentos.",
        },
        implementado: false,
    },
    {
        clave: "004",
        tipo: 3,
        nombre: "Aplicación de saldo a favor por compensación anual",
        alias: ["saldo a favor", "compensacion anual", "ajuste anual"],
        tratamientoIsr: "na",
        resumen: "La devolución al trabajador del ISR que se le retuvo de más en el año.",
        lft: { articulos: "No aplica", texto: "Es una figura fiscal, no laboral." },
        isr: {
            articulos: "Art. 97",
            texto:
                "El patrón que hace el cálculo anual del impuesto debe compensar el saldo a " +
                "favor del trabajador contra las retenciones del mismo o de otros trabajadores, " +
                "y entregarle la diferencia.",
        },
        implementado: false,
    },
];

// ─────────────────────────────────────────────────────────────────────────────
// Resto del catálogo SAT: un renglón por clave, para que la búsqueda encuentre
// cualquier concepto aunque no tenga ficha completa.
// Fuente de nombres: erp_backend/scripts/seedTipoConceptoSat.js
// ─────────────────────────────────────────────────────────────────────────────
const BREVES = [
    // Percepciones
    ["004", 1, "Reembolso de Gastos Médicos Dentales y Hospitalarios", "parcial", "Previsión social: exento si se otorga de forma general.", ["gastos medicos", "reembolso medico", "dentista", "hospital"]],
    ["006", 1, "Caja de ahorro", "parcial", "Previsión social, mismo tratamiento que el fondo de ahorro.", ["caja de ahorro"]],
    ["009", 1, "Contribuciones a Cargo del Trabajador Pagadas por el Patrón", "parcial", "Exento cuando lo paga el patrón por cuenta del trabajador.", ["contribuciones pagadas por el patron"]],
    ["011", 1, "Prima de Seguro de vida", "parcial", "Previsión social: exenta si se otorga de forma general.", ["seguro de vida", "prima de seguro de vida"]],
    ["012", 1, "Seguro de Gastos Médicos Mayores", "parcial", "Previsión social: exento si se otorga de forma general.", ["gastos medicos mayores", "gmm", "seguro de gastos medicos"]],
    ["013", 1, "Cuotas Sindicales Pagadas por el Patrón", "exento", "Exentas cuando las cubre el patrón por cuenta del trabajador.", ["cuotas sindicales pagadas por el patron"]],
    ["015", 1, "Becas para trabajadores y/o hijos", "parcial", "Previsión social: exentas si se otorgan de forma general.", ["beca", "becas", "colegiatura"]],
    ["024", 1, "Seguro de retiro", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["seguro de retiro"]],
    ["026", 1, "Reembolso por funeral", "parcial", "Previsión social: exento si se otorga de forma general.", ["funeral", "reembolso por funeral", "gastos funerarios"]],
    ["027", 1, "Cuotas de seguridad social pagadas por el patrón", "exento", "Art. 93 fracc. XII: exentas para el trabajador.", ["cuotas imss pagadas por el patron", "seguridad social pagada por el patron"]],
    ["030", 1, "Vales de restaurante", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["vales de restaurante", "restaurante"]],
    ["031", 1, "Vales de gasolina", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["vales de gasolina", "gasolina"]],
    ["032", 1, "Vales de ropa", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["vales de ropa", "ropa", "uniforme"]],
    ["033", 1, "Ayuda para renta", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["ayuda de renta", "renta", "vivienda ayuda"]],
    ["034", 1, "Ayuda para artículos escolares", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["utiles escolares", "articulos escolares", "ayuda escolar"]],
    ["035", 1, "Ayuda para anteojos", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["anteojos", "lentes", "ayuda para anteojos"]],
    ["036", 1, "Ayuda para transporte", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["transporte", "ayuda de transporte", "pasajes"]],
    ["037", 1, "Ayuda para gastos de funeral", "parcial", "Previsión social sujeta a los topes del Art. 93.", ["ayuda para funeral", "gastos de funeral"]],
    ["039", 1, "Jubilaciones, pensiones o haberes de retiro", "parcial", "En una sola exhibición: exentas hasta 90 UMA por año de servicio.", ["jubilacion", "pension", "haber de retiro", "retiro"]],
    ["044", 1, "Jubilaciones, pensiones o haberes de retiro en parcialidades", "parcial", "En parcialidades: exentas hasta 15 UMA diarias.", ["jubilacion en parcialidades", "pension en parcialidades"]],
    ["045", 1, "Ingresos en acciones o títulos valor que representan bienes", "gravado", "Gravados por la diferencia entre el valor de mercado y el precio pagado.", ["acciones", "titulos valor", "stock options"]],
    ["047", 1, "Alimentación diferentes a los establecidos en el Art 94 último párrafo LISR", "gravado", "La alimentación que no es comedor de empresa se grava.", ["alimentacion", "comida", "despensa alimentos"]],
    ["048", 1, "Habitación", "gravado", "La habitación proporcionada se grava e integra al SBC.", ["habitacion", "casa", "alojamiento"]],
    ["049", 1, "Premios por asistencia", "gravado", "Gravado al 100%. No integra al SBC hasta el 10% del salario base.", ["premio de asistencia", "asistencia", "bono de asistencia"]],
    ["051", 1, "Pagos por gratificaciones, primas, compensaciones, recompensas u otros a extrabajadores derivados de jubilación en parcialidades", "gravado", "Pagos a extrabajadores jubilados en parcialidades.", ["extrabajadores", "jubilados parcialidades"]],
    ["052", 1, "Pagos que se realicen a extrabajadores que obtengan una jubilación en parcialidades derivados de la ejecución de resoluciones judicial o de un laudo", "gravado", "Derivados de una resolución judicial o laudo.", ["laudo", "resolucion judicial parcialidades"]],
    ["053", 1, "Pagos que se realicen a extrabajadores en una sola exhibición derivados de la ejecución de resoluciones judicial o de un laudo", "gravado", "Derivados de una resolución judicial o laudo, en una sola exhibición.", ["laudo una exhibicion", "resolucion judicial"]],

    // Deducciones
    ["003", 2, "Aportaciones a retiro, cesantía en edad avanzada y vejez", "deduccion", "Retención al trabajador por el ramo de retiro.", ["retiro cesantia vejez", "rcv"]],
    ["005", 2, "Aportaciones a Fondo de vivienda", "deduccion", "Aportación del trabajador al fondo de vivienda.", ["aportacion vivienda", "fondo de vivienda"]],
    ["008", 2, "Renta", "deduccion", "Descuento por renta de habitación proporcionada por el patrón (Art. 110-VII LFT).", ["renta", "descuento de renta"]],
    ["010", 2, "Pago por crédito de vivienda", "deduccion", "Descuento por crédito de vivienda distinto al INFONAVIT.", ["credito de vivienda", "pago credito vivienda"]],
    ["012", 2, "Anticipo de salarios", "deduccion", "Recuperación de un anticipo (Art. 110-I LFT).", ["anticipo", "anticipo de salarios", "adelanto"]],
    ["013", 2, "Pagos hechos con exceso al trabajador", "deduccion", "Recuperación de un pago en exceso (Art. 110-I LFT).", ["pago en exceso", "pagos con exceso"]],
    ["014", 2, "Errores", "deduccion", "Descuento por errores, dentro de los límites del Art. 110 LFT.", ["errores"]],
    ["015", 2, "Pérdidas", "deduccion", "Descuento por pérdidas, dentro de los límites del Art. 110 LFT.", ["perdidas"]],
    ["016", 2, "Averías", "deduccion", "Descuento por averías, dentro de los límites del Art. 110 LFT.", ["averias"]],
    ["017", 2, "Adquisición de artículos producidos por la empresa o establecimiento", "deduccion", "Compra de productos de la empresa (Art. 110-IV LFT).", ["compra de articulos", "productos de la empresa"]],
    ["018", 2, "Cuotas para la constitución y fomento de sociedades cooperativas y de cajas de ahorro", "deduccion", "Aportación del trabajador a la caja de ahorro (Art. 110-IV LFT).", ["cooperativa", "caja de ahorro aportacion"]],
    ["021", 2, "Cuotas obligatorias patronales", "deduccion", "Informativo de las cuotas a cargo del patrón.", ["cuotas patronales"]],
    ["022", 2, "Impuestos Locales", "deduccion", "Impuestos estatales a cargo del trabajador.", ["impuestos locales", "impuesto estatal"]],
    ["023", 2, "Aportaciones voluntarias", "deduccion", "Aportaciones voluntarias del trabajador al retiro.", ["aportaciones voluntarias", "afore voluntario"]],
    ["101", 2, "ISR Retenido de ejercicio anterior", "deduccion", "Retención de ISR correspondiente a un ejercicio anterior.", ["isr ejercicio anterior"]],
    ["107", 2, "Ajuste al Subsidio Causado", "deduccion", "Ajuste del subsidio causado en el mes.", ["ajuste subsidio causado"]],

    // Otros Pagos
    ["001", 3, "Reintegro de ISR pagado en exceso (siempre que no haya sido enterado al SAT)", "na", "Devolución de ISR retenido de más en el mismo ejercicio.", ["reintegro isr", "isr pagado en exceso"]],
    ["005", 3, "Reintegro de ISR retenido en exceso de ejercicio anterior (siempre que no haya sido enterado al SAT)", "na", "Devolución de ISR de un ejercicio anterior.", ["reintegro isr ejercicio anterior"]],
    ["006", 3, "Alimentos en bienes (Servicios de comedor y comida) Art 94 último párrafo LISR", "na", "Comedor de empresa: no se considera ingreso.", ["comedor", "alimentos en bienes", "comida de empresa"]],
    ["007", 3, "ISR ajustado por subsidio", "na", "Ajuste del ISR por el subsidio del mes.", ["isr ajustado por subsidio"]],
    ["008", 3, "Subsidio efectivamente entregado que no correspondía (Apéndice 7 de la guía de llenado de nómina)", "na", "Recuperación de subsidio entregado de más.", ["subsidio que no correspondia"]],
    ["009", 3, "Reembolso de descuentos efectuados para el crédito de vivienda", "na", "Devolución de descuentos de INFONAVIT hechos de más.", ["reembolso infonavit", "reembolso credito vivienda"]],
    ["999", 3, "Pagos distintos a los listados que no deben considerarse como ingreso por sueldos, salarios o ingresos asimilados", "na", "La clave que usa Biwo para entregar un préstamo dentro de la nómina: sube el neto sin ser ingreso. En la pestaña Contabilidad del concepto hay que encender «Préstamo entregado en la nómina» y ligarlo a la deducción que cobra las amortizaciones, para que el cargo de la entrega y los abonos del cobro caigan en la misma cuenta por cobrar del empleado.", ["prestamo", "pagos distintos", "no es ingreso"]],
    // Claves de ajuste. Corrigen en un recibo posterior lo declarado de más o de menos
    // en otro del mismo ejercicio; el catálogo SAT las trata todas como deducciones.
    ["024", 2, "Ajuste en Gratificación Anual (Aguinaldo) Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Gratificación Anual (Aguinaldo)\" ya declarada en un recibo anterior del mismo ejercicio.", ["gratificacion anual (aguinaldo)", "ajuste", "ajuste en gratificacion anual (aguinaldo) exento"]],
    ["025", 2, "Ajuste en Gratificación Anual (Aguinaldo) Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Gratificación Anual (Aguinaldo)\" ya declarada en un recibo anterior del mismo ejercicio.", ["gratificacion anual (aguinaldo)", "ajuste", "ajuste en gratificacion anual (aguinaldo) gravado"]],
    ["026", 2, "Ajuste en Participación de los Trabajadores en las Utilidades PTU Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Participación de los Trabajadores en las Utilidades PTU\" ya declarada en un recibo anterior del mismo ejercicio.", ["participacion de los trabajadores en las utilidades ptu", "ajuste", "ajuste en participacion de los trabajadores en las utilidades ptu exento"]],
    ["027", 2, "Ajuste en Participación de los Trabajadores en las Utilidades PTU Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Participación de los Trabajadores en las Utilidades PTU\" ya declarada en un recibo anterior del mismo ejercicio.", ["participacion de los trabajadores en las utilidades ptu", "ajuste", "ajuste en participacion de los trabajadores en las utilidades ptu gravado"]],
    ["028", 2, "Ajuste en Reembolso de Gastos Médicos Dentales y Hospitalarios Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Reembolso de Gastos Médicos Dentales y Hospitalarios\" ya declarada en un recibo anterior del mismo ejercicio.", ["reembolso de gastos medicos dentales y hospitalarios", "ajuste", "ajuste en reembolso de gastos medicos dentales y hospitalarios exento"]],
    ["029", 2, "Ajuste en Fondo de ahorro Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Fondo de ahorro\" ya declarada en un recibo anterior del mismo ejercicio.", ["fondo de ahorro", "ajuste", "ajuste en fondo de ahorro exento"]],
    ["030", 2, "Ajuste en Caja de ahorro Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Caja de ahorro\" ya declarada en un recibo anterior del mismo ejercicio.", ["caja de ahorro", "ajuste", "ajuste en caja de ahorro exento"]],
    ["031", 2, "Ajuste en Contribuciones a Cargo del Trabajador Pagadas por el Patrón Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Contribuciones a Cargo del Trabajador Pagadas por el Patrón\" ya declarada en un recibo anterior del mismo ejercicio.", ["contribuciones a cargo del trabajador pagadas por el patron", "ajuste", "ajuste en contribuciones a cargo del trabajador pagadas por el patron exento"]],
    ["032", 2, "Ajuste en Premios por puntualidad Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Premios por puntualidad\" ya declarada en un recibo anterior del mismo ejercicio.", ["premios por puntualidad", "ajuste", "ajuste en premios por puntualidad gravado"]],
    ["033", 2, "Ajuste en Prima de Seguro de vida Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Prima de Seguro de vida\" ya declarada en un recibo anterior del mismo ejercicio.", ["prima de seguro de vida", "ajuste", "ajuste en prima de seguro de vida exento"]],
    ["034", 2, "Ajuste en Seguro de Gastos Médicos Mayores Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Seguro de Gastos Médicos Mayores\" ya declarada en un recibo anterior del mismo ejercicio.", ["seguro de gastos medicos mayores", "ajuste", "ajuste en seguro de gastos medicos mayores exento"]],
    ["035", 2, "Ajuste en Cuotas Sindicales Pagadas por el Patrón Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Cuotas Sindicales Pagadas por el Patrón\" ya declarada en un recibo anterior del mismo ejercicio.", ["cuotas sindicales pagadas por el patron", "ajuste", "ajuste en cuotas sindicales pagadas por el patron exento"]],
    ["036", 2, "Ajuste en Subsidios por incapacidad Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Subsidios por incapacidad\" ya declarada en un recibo anterior del mismo ejercicio.", ["subsidios por incapacidad", "ajuste", "ajuste en subsidios por incapacidad exento"]],
    ["037", 2, "Ajuste en Becas para trabajadores y/o hijos Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Becas para trabajadores y/o hijos\" ya declarada en un recibo anterior del mismo ejercicio.", ["becas para trabajadores y/o hijos", "ajuste", "ajuste en becas para trabajadores y/o hijos exento"]],
    ["038", 2, "Ajuste en Horas extra Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Horas extra\" ya declarada en un recibo anterior del mismo ejercicio.", ["horas extra", "ajuste", "ajuste en horas extra exento"]],
    ["039", 2, "Ajuste en Horas extra Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Horas extra\" ya declarada en un recibo anterior del mismo ejercicio.", ["horas extra", "ajuste", "ajuste en horas extra gravado"]],
    ["040", 2, "Ajuste en Prima dominical Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Prima dominical\" ya declarada en un recibo anterior del mismo ejercicio.", ["prima dominical", "ajuste", "ajuste en prima dominical exento"]],
    ["041", 2, "Ajuste en Prima dominical Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Prima dominical\" ya declarada en un recibo anterior del mismo ejercicio.", ["prima dominical", "ajuste", "ajuste en prima dominical gravado"]],
    ["042", 2, "Ajuste en Prima vacacional Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Prima vacacional\" ya declarada en un recibo anterior del mismo ejercicio.", ["prima vacacional", "ajuste", "ajuste en prima vacacional exento"]],
    ["043", 2, "Ajuste en Prima vacacional Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Prima vacacional\" ya declarada en un recibo anterior del mismo ejercicio.", ["prima vacacional", "ajuste", "ajuste en prima vacacional gravado"]],
    ["044", 2, "Ajuste en Prima por antigüedad Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Prima por antigüedad\" ya declarada en un recibo anterior del mismo ejercicio.", ["prima por antiguedad", "ajuste", "ajuste en prima por antiguedad exento"]],
    ["045", 2, "Ajuste en Prima por antigüedad Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Prima por antigüedad\" ya declarada en un recibo anterior del mismo ejercicio.", ["prima por antiguedad", "ajuste", "ajuste en prima por antiguedad gravado"]],
    ["046", 2, "Ajuste en Pagos por separación Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Pagos por separación\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos por separacion", "ajuste", "ajuste en pagos por separacion exento"]],
    ["047", 2, "Ajuste en Pagos por separación Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Pagos por separación\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos por separacion", "ajuste", "ajuste en pagos por separacion gravado"]],
    ["048", 2, "Ajuste en Seguro de retiro Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Seguro de retiro\" ya declarada en un recibo anterior del mismo ejercicio.", ["seguro de retiro", "ajuste", "ajuste en seguro de retiro exento"]],
    ["049", 2, "Ajuste en Indemnizaciones Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Indemnizaciones\" ya declarada en un recibo anterior del mismo ejercicio.", ["indemnizaciones", "ajuste", "ajuste en indemnizaciones exento"]],
    ["050", 2, "Ajuste en Indemnizaciones Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Indemnizaciones\" ya declarada en un recibo anterior del mismo ejercicio.", ["indemnizaciones", "ajuste", "ajuste en indemnizaciones gravado"]],
    ["051", 2, "Ajuste en Reembolso por funeral Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Reembolso por funeral\" ya declarada en un recibo anterior del mismo ejercicio.", ["reembolso por funeral", "ajuste", "ajuste en reembolso por funeral exento"]],
    ["052", 2, "Ajuste en Cuotas de seguridad social pagadas por el patrón Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Cuotas de seguridad social pagadas por el patrón\" ya declarada en un recibo anterior del mismo ejercicio.", ["cuotas de seguridad social pagadas por el patron", "ajuste", "ajuste en cuotas de seguridad social pagadas por el patron exento"]],
    ["053", 2, "Ajuste en Comisiones Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Comisiones\" ya declarada en un recibo anterior del mismo ejercicio.", ["comisiones", "ajuste", "ajuste en comisiones gravado"]],
    ["054", 2, "Ajuste en Vales de despensa Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Vales de despensa\" ya declarada en un recibo anterior del mismo ejercicio.", ["vales de despensa", "ajuste", "ajuste en vales de despensa exento"]],
    ["055", 2, "Ajuste en Vales de restaurante Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Vales de restaurante\" ya declarada en un recibo anterior del mismo ejercicio.", ["vales de restaurante", "ajuste", "ajuste en vales de restaurante exento"]],
    ["056", 2, "Ajuste en Vales de gasolina Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Vales de gasolina\" ya declarada en un recibo anterior del mismo ejercicio.", ["vales de gasolina", "ajuste", "ajuste en vales de gasolina exento"]],
    ["057", 2, "Ajuste en Vales de ropa Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Vales de ropa\" ya declarada en un recibo anterior del mismo ejercicio.", ["vales de ropa", "ajuste", "ajuste en vales de ropa exento"]],
    ["058", 2, "Ajuste en Ayuda para renta Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Ayuda para renta\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para renta", "ajuste", "ajuste en ayuda para renta exento"]],
    ["059", 2, "Ajuste en Ayuda para artículos escolares Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Ayuda para artículos escolares\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para articulos escolares", "ajuste", "ajuste en ayuda para articulos escolares exento"]],
    ["060", 2, "Ajuste en Ayuda para anteojos Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Ayuda para anteojos\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para anteojos", "ajuste", "ajuste en ayuda para anteojos exento"]],
    ["061", 2, "Ajuste en Ayuda para transporte Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Ayuda para transporte\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para transporte", "ajuste", "ajuste en ayuda para transporte exento"]],
    ["062", 2, "Ajuste en Ayuda para gastos de funeral Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Ayuda para gastos de funeral\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para gastos de funeral", "ajuste", "ajuste en ayuda para gastos de funeral exento"]],
    ["063", 2, "Ajuste en Otros ingresos por salarios Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Otros ingresos por salarios\" ya declarada en un recibo anterior del mismo ejercicio.", ["otros ingresos por salarios", "ajuste", "ajuste en otros ingresos por salarios exento"]],
    ["064", 2, "Ajuste en Otros ingresos por salarios Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Otros ingresos por salarios\" ya declarada en un recibo anterior del mismo ejercicio.", ["otros ingresos por salarios", "ajuste", "ajuste en otros ingresos por salarios gravado"]],
    ["065", 2, "Ajuste en Jubilaciones, pensiones o haberes de retiro en una sola exhibición Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Jubilaciones, pensiones o haberes de retiro en una sola exhibición\" ya declarada en un recibo anterior del mismo ejercicio.", ["jubilaciones, pensiones o haberes de retiro en una sola exhibicion", "ajuste", "ajuste en jubilaciones, pensiones o haberes de retiro en una sola exhibicion exento"]],
    ["066", 2, "Ajuste en Jubilaciones, pensiones o haberes de retiro en una sola exhibición Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Jubilaciones, pensiones o haberes de retiro en una sola exhibición\" ya declarada en un recibo anterior del mismo ejercicio.", ["jubilaciones, pensiones o haberes de retiro en una sola exhibicion", "ajuste", "ajuste en jubilaciones, pensiones o haberes de retiro en una sola exhibicion gravado"]],
    ["067", 2, "Ajuste en Pagos por separación Acumulable", "deduccion", "Clave de ajuste: corrige la parte acumulable de \"Pagos por separación\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos por separacion", "ajuste", "ajuste en pagos por separacion acumulable"]],
    ["068", 2, "Ajuste en Pagos por separación No acumulable", "deduccion", "Clave de ajuste: corrige la parte no acumulable de \"Pagos por separación\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos por separacion", "ajuste", "ajuste en pagos por separacion no acumulable"]],
    ["069", 2, "Ajuste en Jubilaciones, pensiones o haberes de retiro en parcialidades Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Jubilaciones, pensiones o haberes de retiro en parcialidades\" ya declarada en un recibo anterior del mismo ejercicio.", ["jubilaciones, pensiones o haberes de retiro en parcialidades", "ajuste", "ajuste en jubilaciones, pensiones o haberes de retiro en parcialidades exento"]],
    ["070", 2, "Ajuste en Jubilaciones, pensiones o haberes de retiro en parcialidades Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Jubilaciones, pensiones o haberes de retiro en parcialidades\" ya declarada en un recibo anterior del mismo ejercicio.", ["jubilaciones, pensiones o haberes de retiro en parcialidades", "ajuste", "ajuste en jubilaciones, pensiones o haberes de retiro en parcialidades gravado"]],
    ["071", 2, "Ajuste en Subsidio para el empleo (efectivamente entregado al trabajador)", "deduccion", "Clave de ajuste: corrige \"Subsidio para el empleo (efectivamente entregado al trabajador)\" ya declarada en un recibo anterior del mismo ejercicio.", ["subsidio para el empleo (efectivamente entregado al trabajador)", "ajuste", "ajuste en subsidio para el empleo (efectivamente entregado al trabajador)"]],
    ["072", 2, "Ajuste en Ingresos en acciones o títulos valor que representan bienes Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Ingresos en acciones o títulos valor que representan bienes\" ya declarada en un recibo anterior del mismo ejercicio.", ["ingresos en acciones o titulos valor que representan bienes", "ajuste", "ajuste en ingresos en acciones o titulos valor que representan bienes exento"]],
    ["073", 2, "Ajuste en Ingresos en acciones o títulos valor que representan bienes Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Ingresos en acciones o títulos valor que representan bienes\" ya declarada en un recibo anterior del mismo ejercicio.", ["ingresos en acciones o titulos valor que representan bienes", "ajuste", "ajuste en ingresos en acciones o titulos valor que representan bienes gravado"]],
    ["074", 2, "Ajuste en Alimentación Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Alimentación\" ya declarada en un recibo anterior del mismo ejercicio.", ["alimentacion", "ajuste", "ajuste en alimentacion exento"]],
    ["075", 2, "Ajuste en Alimentación Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Alimentación\" ya declarada en un recibo anterior del mismo ejercicio.", ["alimentacion", "ajuste", "ajuste en alimentacion gravado"]],
    ["076", 2, "Ajuste en Habitación Exento", "deduccion", "Clave de ajuste: corrige la parte exento de \"Habitación\" ya declarada en un recibo anterior del mismo ejercicio.", ["habitacion", "ajuste", "ajuste en habitacion exento"]],
    ["077", 2, "Ajuste en Habitación Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Habitación\" ya declarada en un recibo anterior del mismo ejercicio.", ["habitacion", "ajuste", "ajuste en habitacion gravado"]],
    ["078", 2, "Ajuste en Premios por asistencia", "deduccion", "Clave de ajuste: corrige \"Premios por asistencia\" ya declarada en un recibo anterior del mismo ejercicio.", ["premios por asistencia", "ajuste", "ajuste en premios por asistencia"]],
    ["079", 2, "Ajuste en Pagos distintos a los listados y que no deben considerarse como ingreso por sueldos, salarios o ingresos asimilados", "deduccion", "Clave de ajuste: corrige \"Pagos distintos a los listados y que no deben considerarse como ingreso por sueldos, salarios o ingresos asimilados\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos distintos a los listados y que no deben considerarse como ingreso por sueldos, salarios o ingresos asimilados", "ajuste", "ajuste en pagos distintos a los listados y que no deben considerarse como ingreso por sueldos, salarios o ingresos asimilados"]],
    ["080", 2, "Ajuste en Viáticos gravados", "deduccion", "Clave de ajuste: corrige \"Viáticos gravados\" ya declarada en un recibo anterior del mismo ejercicio.", ["viaticos gravados", "ajuste", "ajuste en viaticos gravados"]],
    ["081", 2, "Ajuste en Viáticos (entregados al trabajador)", "deduccion", "Clave de ajuste: corrige \"Viáticos (entregados al trabajador)\" ya declarada en un recibo anterior del mismo ejercicio.", ["viaticos (entregados al trabajador)", "ajuste", "ajuste en viaticos (entregados al trabajador)"]],
    ["082", 2, "Ajuste en Fondo de ahorro Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Fondo de ahorro\" ya declarada en un recibo anterior del mismo ejercicio.", ["fondo de ahorro", "ajuste", "ajuste en fondo de ahorro gravado"]],
    ["083", 2, "Ajuste en Caja de ahorro Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Caja de ahorro\" ya declarada en un recibo anterior del mismo ejercicio.", ["caja de ahorro", "ajuste", "ajuste en caja de ahorro gravado"]],
    ["084", 2, "Ajuste en Prima de Seguro de vida Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Prima de Seguro de vida\" ya declarada en un recibo anterior del mismo ejercicio.", ["prima de seguro de vida", "ajuste", "ajuste en prima de seguro de vida gravado"]],
    ["085", 2, "Ajuste en Seguro de Gastos Médicos Mayores Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Seguro de Gastos Médicos Mayores\" ya declarada en un recibo anterior del mismo ejercicio.", ["seguro de gastos medicos mayores", "ajuste", "ajuste en seguro de gastos medicos mayores gravado"]],
    ["086", 2, "Ajuste en Subsidios por incapacidad Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Subsidios por incapacidad\" ya declarada en un recibo anterior del mismo ejercicio.", ["subsidios por incapacidad", "ajuste", "ajuste en subsidios por incapacidad gravado"]],
    ["087", 2, "Ajuste en Becas para trabajadores y/o hijos Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Becas para trabajadores y/o hijos\" ya declarada en un recibo anterior del mismo ejercicio.", ["becas para trabajadores y/o hijos", "ajuste", "ajuste en becas para trabajadores y/o hijos gravado"]],
    ["088", 2, "Ajuste en Seguro de retiro Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Seguro de retiro\" ya declarada en un recibo anterior del mismo ejercicio.", ["seguro de retiro", "ajuste", "ajuste en seguro de retiro gravado"]],
    ["089", 2, "Ajuste en Vales de despensa Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Vales de despensa\" ya declarada en un recibo anterior del mismo ejercicio.", ["vales de despensa", "ajuste", "ajuste en vales de despensa gravado"]],
    ["090", 2, "Ajuste en Vales de restaurante Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Vales de restaurante\" ya declarada en un recibo anterior del mismo ejercicio.", ["vales de restaurante", "ajuste", "ajuste en vales de restaurante gravado"]],
    ["091", 2, "Ajuste en Vales de gasolina Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Vales de gasolina\" ya declarada en un recibo anterior del mismo ejercicio.", ["vales de gasolina", "ajuste", "ajuste en vales de gasolina gravado"]],
    ["092", 2, "Ajuste en Vales de ropa Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Vales de ropa\" ya declarada en un recibo anterior del mismo ejercicio.", ["vales de ropa", "ajuste", "ajuste en vales de ropa gravado"]],
    ["093", 2, "Ajuste en Ayuda para renta Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Ayuda para renta\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para renta", "ajuste", "ajuste en ayuda para renta gravado"]],
    ["094", 2, "Ajuste en Ayuda para artículos escolares Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Ayuda para artículos escolares\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para articulos escolares", "ajuste", "ajuste en ayuda para articulos escolares gravado"]],
    ["095", 2, "Ajuste en Ayuda para anteojos Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Ayuda para anteojos\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para anteojos", "ajuste", "ajuste en ayuda para anteojos gravado"]],
    ["096", 2, "Ajuste en Ayuda para transporte Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Ayuda para transporte\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para transporte", "ajuste", "ajuste en ayuda para transporte gravado"]],
    ["097", 2, "Ajuste en Ayuda para gastos de funeral Gravado", "deduccion", "Clave de ajuste: corrige la parte gravado de \"Ayuda para gastos de funeral\" ya declarada en un recibo anterior del mismo ejercicio.", ["ayuda para gastos de funeral", "ajuste", "ajuste en ayuda para gastos de funeral gravado"]],
    ["098", 2, "Ajuste a ingresos asimilados a salarios gravados", "deduccion", "Clave de ajuste: corrige \"ingresos asimilados a salarios gravados\" ya declarada en un recibo anterior del mismo ejercicio.", ["ingresos asimilados a salarios gravados", "ajuste", "ajuste a ingresos asimilados a salarios gravados"]],
    ["099", 2, "Ingresos por sueldos y salarios gravados", "deduccion", "Clave de ajuste: descuenta ingresos por sueldos y salarios ya declarados como gravados en un recibo anterior del mismo ejercicio.", ["ingresos por sueldos y salarios gravados", "ajuste", "sueldos gravados"]],
    ["100", 2, "Ajuste en Viáticos exentos", "deduccion", "Clave de ajuste: corrige \"Viáticos exentos\" ya declarada en un recibo anterior del mismo ejercicio.", ["viaticos exentos", "ajuste", "ajuste en viaticos exentos"]],
    ["102", 2, "Ajuste a pagos por gratificaciones, primas, compensaciones, recompensas u otros a extrabajadores derivados de jubilación en parcialidades, gravados", "deduccion", "Clave de ajuste: corrige \"pagos por gratificaciones, primas, compensaciones, recompensas u otros a extrabajadores derivados de jubilación en parcialidades, gravados\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos por gratificaciones, primas, compensaciones, recompensas u otros a extrabajadores derivados de jubilacion en parcialidades, gravados", "ajuste", "ajuste a pagos por gratificaciones, primas, compensaciones, recompensas u otros a extrabajadores derivados de jubilacion en parcialidades, gravados"]],
    ["103", 2, "Ajuste a pagos que se realicen a extrabajadores que obtengan una jubilación en parcialidades derivados de la ejecución de una resolución judicial o de un laudo gravados", "deduccion", "Clave de ajuste: corrige \"pagos que se realicen a extrabajadores que obtengan una jubilación en parcialidades derivados de la ejecución de una resolución judicial o de un laudo gravados\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos que se realicen a extrabajadores que obtengan una jubilacion en parcialidades derivados de la ejecucion de una resolucion judicial o de un laudo gravados", "ajuste", "ajuste a pagos que se realicen a extrabajadores que obtengan una jubilacion en parcialidades derivados de la ejecucion de una resolucion judicial o de un laudo gravados"]],
    ["104", 2, "Ajuste a pagos que se realicen a extrabajadores que obtengan una jubilación en parcialidades derivados de la ejecución de una resolución judicial o de un laudo exentos", "deduccion", "Clave de ajuste: corrige \"pagos que se realicen a extrabajadores que obtengan una jubilación en parcialidades derivados de la ejecución de una resolución judicial o de un laudo exentos\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos que se realicen a extrabajadores que obtengan una jubilacion en parcialidades derivados de la ejecucion de una resolucion judicial o de un laudo exentos", "ajuste", "ajuste a pagos que se realicen a extrabajadores que obtengan una jubilacion en parcialidades derivados de la ejecucion de una resolucion judicial o de un laudo exentos"]],
    ["105", 2, "Ajuste a pagos que se realicen a extrabajadores en una sola exhibición derivados de la ejecución de una resolución judicial o de un laudo exentos", "deduccion", "Clave de ajuste: corrige \"pagos que se realicen a extrabajadores en una sola exhibición derivados de la ejecución de una resolución judicial o de un laudo exentos\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos que se realicen a extrabajadores en una sola exhibicion derivados de la ejecucion de una resolucion judicial o de un laudo exentos", "ajuste", "ajuste a pagos que se realicen a extrabajadores en una sola exhibicion derivados de la ejecucion de una resolucion judicial o de un laudo exentos"]],
    ["106", 2, "Ajuste a pagos que se realicen a extrabajadores que obtengan una jubilación en parcialidades derivados de la ejecución de una resolución judicial o de un laudo exentos (acumulable)", "deduccion", "Clave de ajuste: corrige \"pagos que se realicen a extrabajadores que obtengan una jubilación en parcialidades derivados de la ejecución de una resolución judicial o de un laudo exentos (acumulable)\" ya declarada en un recibo anterior del mismo ejercicio.", ["pagos que se realicen a extrabajadores que obtengan una jubilacion en parcialidades derivados de la ejecucion de una resolucion judicial o de un laudo exentos (acumulable)", "ajuste", "ajuste a pagos que se realicen a extrabajadores que obtengan una jubilacion en parcialidades derivados de la ejecucion de una resolucion judicial o de un laudo exentos (acumulable)"]],
].map(([clave, tipo, nombre, tratamientoIsr, resumen, alias]) => ({
    clave,
    tipo,
    nombre,
    alias,
    tratamientoIsr,
    resumen,
    // Sin `implementado`: de estos conceptos no afirmamos si el asistente los siembra o no.
    // Algunos sí los crea (Otro Pago 999 para el préstamo, la deducción 107) y decir lo
    // contrario sería mentirle al usuario.
}));

// Percepciones primero, luego deducciones y otros pagos; dentro de cada tipo, por clave.
export const CONCEPTOS = [...FICHAS, ...BREVES].sort(
    (a, b) => a.tipo - b.tipo || a.clave.localeCompare(b.clave)
);

// ─────────────────────────────────────────────────────────────────────────────
// Búsqueda
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Minúsculas y sin acentos, para que "gratificacion" encuentre "Gratificación Anual".
 * Mismo criterio que normalizarDescripcion en wizardNomina.js del backend.
 */
export const normalizar = (texto) =>
    (texto || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();

// Texto contra el que se busca cada concepto, en tres capas. Se calcula una vez, no en
// cada tecla. La separación importa para el orden: "gratificacion" tiene que llevar al
// aguinaldo, no al Sueldo, que sólo la menciona porque el Art. 84 LFT la nombra de paso.
const INDICE = new Map(
    CONCEPTOS.map((c) => [
        c,
        {
            titulo: normalizar([c.clave, c.nombre, (c.alias || []).join(" ")].join(" ")),
            resumen: normalizar(c.resumen || ""),
            cuerpo: normalizar(
                [
                    c.lft && c.lft.texto,
                    c.lft && c.lft.articulos,
                    c.isr && c.isr.texto,
                    c.isr && c.isr.articulos,
                    c.imss && c.imss.texto,
                    c.imss && c.imss.articulos,
                ]
                    .filter(Boolean)
                    .join(" ")
            ),
        },
    ])
);

/** La ficha de una clave SAT. La clave se repite entre catálogos, así que el tipo es obligatorio. */
export const buscarConcepto = (clave, tipo) => {
    if (!clave || !tipo) return null;
    return CONCEPTOS.find((c) => c.clave === String(clave) && c.tipo === Number(tipo)) || null;
};

// Qué tan buena es la coincidencia: pesa más el nombre que el texto de la ley, y a
// igualdad gana la ficha documentada sobre el renglón breve. 0 = no coincide.
const puntaje = (concepto, palabras) => {
    const ix = INDICE.get(concepto);
    if (!ix) return 0;
    const todas = (campo) => palabras.every((p) => campo.includes(p));
    let p = 0;
    if (todas(ix.titulo)) p = 4;
    else if (todas(ix.resumen)) p = 2;
    else if (todas(`${ix.titulo} ${ix.resumen} ${ix.cuerpo}`)) p = 1;
    else return 0;
    return concepto.lft ? p + 1 : p;
};

/**
 * Conceptos que coinciden con el texto. Todas las palabras tienen que aparecer, en
 * cualquier orden, para que "prima vacacional" no traiga toda la lista de primas.
 *
 * Sin texto se respeta el orden del catálogo SAT, que es como la gente lo espera al
 * hojearlo. Con texto manda el puntaje.
 */
export const filtrarConceptos = (texto, tipo) => {
    const base = tipo ? CONCEPTOS.filter((c) => c.tipo === Number(tipo)) : CONCEPTOS;
    const palabras = normalizar(texto).split(/\s+/).filter(Boolean);
    if (palabras.length === 0) return base;
    return base
        .map((c) => ({ c, p: puntaje(c, palabras) }))
        .filter((x) => x.p > 0)
        .sort((a, b) => b.p - a.p)
        .map((x) => x.c);
};
